import React from 'react';
import {
	Flex,
	InputNumberProps,
} from 'antd';
import { PeriodUnits } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import Select, { SelectProps } from '@client/components/Select';
import styles from './styles/PeriodSelector.module.css';
import NumericInput from './NumericInput';

export type PeriodValue = [number, Values<typeof PeriodUnits>, number];

export type PeriodSelectorProps = {
	value: PeriodValue;
	onChange: (newValue: PeriodValue) => void;
	labelRight?: string | React.ReactElement;
	valueInputProps?: InputNumberProps<number>;
	unitInputProps?: Omit<SelectProps<Values<typeof PeriodUnits>>, 'value' | 'onChange'>;
	varianceInputProps?: InputNumberProps<number>;
	disabled?: boolean;
};

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
	onChange,
	value,
	labelRight,
	valueInputProps = {},
	unitInputProps = {},
	varianceInputProps = {},
	disabled = false,
}) => {
	const variance = value[2] || 0;
	const unit = value[1] || PeriodUnits.MONTHS;
	const periodValue = value[0];

	return (
		<div className={styles.wrapper}>
			<Flex gap={1}>
				<NumericInput
					{...valueInputProps}
					onChange={(newPeriodValue) => onChange([newPeriodValue ?? 0, unit, variance])}
					placeholder="Value"
					value={periodValue}
					className={styles.valueInput}
					asAsync
					min={0}
					disabled={disabled}
				/>
				<Select
					{...unitInputProps}
					className={styles.unitSelector}
					value={unit}
					onChange={(newUnit) => onChange([
						periodValue,
						newUnit as Values<typeof PeriodUnits>,
						variance,
					])}
					options={Object.values(PeriodUnits).map((u) => ({
						label: u.toLowerCase(),
						value: u,
					}))}
					disabled={disabled}
				/>
				<NumericInput
					{...varianceInputProps}
					prefix="±"
					suffix="d"
					asAsync
					className={styles.varianceInput}
					value={variance}
					onChange={(newVariance) => onChange([periodValue, unit, newVariance ?? 0])}
					min={0}
					max={999}
					disabled={disabled}
				/>
			</Flex>
			{labelRight != null && (
				<span className={styles.labelRight}>{labelRight}</span>
			)}
		</div>
	);
};

export default PeriodSelector;
